import React from "react";

// components
import Link from "@/components/Link";
import CardCarousel, { ICardCarousel } from "@/components/CardCarousel";
import LocationSpaceCardFavourite from "./LocationSpaceCardFavourite";

// helpers
import { formatCurrency } from "@/helpers/currencyUtils";
import { SPACE_TYPE_C } from "@/globals/spaceListingTypes.constants";

// interfaces
import { ILocationSpaceItem } from "@/types/api/Locations";
import { TListingType } from "@/types/Global"

// assets
import styles from "@/assets/styles/pages/location-single-page/location-single-page-components.module.scss";


export interface ILocationSpaceCard extends React.HTMLAttributes<HTMLElement> {
  images?: ICardCarousel["items"];
  link?: string;
  fullSpaceData: ILocationSpaceItem;
  isShowFxPrice: boolean;
  currency: string;
  popoverClassName?: string;
  onAddToCampaign: () => void;
  handleSendAnalytics?: () => void;
}

const LocationSpaceCard = (props: ILocationSpaceCard) => {
  const {
    images,
    link,
    fullSpaceData,
    isShowFxPrice,
    currency = "GBP",
    popoverClassName,
    onAddToCampaign,
    handleSendAnalytics,
    ...rest
  } = props;

  const {
    dimension,
    type,
    listingType,
    rateCard,
    isNew,
    campaigns,
    spaceAssetType,
  } = fullSpaceData;

  const isFavorite = campaigns.length > 0;
  const isMediaOpportunity = spaceAssetType === "media_opportunity";

  // renders
  const renderPrice = () => {
    const priceClassNames = styles["locl-location-page-space-card__price"];
    const priceValueClassNames = styles["locl-location-page-space-card__price__value"];
    const fxPriceClassNames = styles["locl-location-page-space-card__price__indicate-fx"];


    if (listingType === SPACE_TYPE_C) {
      return (
        <div className={priceClassNames} data-testid="locl-location-page-space-card-price">
          <span className={priceValueClassNames}>Price on application</span>
          {isShowFxPrice && <span className={fxPriceClassNames}>Indicative FX price</span>}
        </div>
      );
    }

    if (!rateCard.lowestDailyPrice || !rateCard.highestDailyPrice) {
      return null;
    }

    if (rateCard?.lowestDailyPrice && rateCard?.lowestDailyPrice[currency] === rateCard?.highestDailyPrice[currency]) {
      return (
        <div className={priceClassNames} data-testid="locl-location-page-space-card-price">
          <span className={priceValueClassNames}>
            {formatCurrency(rateCard?.lowestDailyPrice[currency], currency)}/day
          </span>
          {isShowFxPrice && <span className={fxPriceClassNames}>Indicative FX price</span>}
        </div>
      )
    }

    return (
      <div className={priceClassNames} data-testid="locl-location-page-space-card-price">
        {rateCard?.lowestDailyPrice && rateCard?.highestDailyPrice && (listingType as TListingType) !== SPACE_TYPE_C
          ? <span className={priceValueClassNames}>
            {formatCurrency(rateCard?.lowestDailyPrice[currency], currency)}
            {" - "}
            {(formatCurrency(rateCard?.highestDailyPrice[currency], currency) as string)}/day
          </span>
          : null}
        {isShowFxPrice && <span className={fxPriceClassNames}>Indicative FX price</span>}
      </div>
    )
  };

  const renderDimensions = () => {
    const spaceTypeToString = type ? "External" : "Internal";

    const dimensionData = [
      !!dimension.width && `${dimension.width}(w)`,
      !!dimension.length && `${dimension.length}(l)`,
      !!dimension.height && `${dimension.height}(h)`,
    ].filter(Boolean).join(" x ");
  
    return dimensionData
      ? <> {dimensionData} <br/> {spaceTypeToString} </>
      : spaceTypeToString;
  };

  return (
    <Link
      className={styles["locl-location-page-space-card"]}
      href={link as string}
      data-testid="locl-space-card" // don't modify this data-testid, it require for a/b tests
      {...rest}
    >
      <div className={styles["locl-location-page-space-card__content"]}>
        <div className={styles["locl-location-page-space-card__carousel"]}>
          <CardCarousel items={images} imgWidth={354} imgHeight={222} />
          {
            isNew && (
              <div
                className={styles["locl-location-page-space-card__new-badge"]}
                data-testid="locl-space-card-new-badge"
              >
                New
              </div>
            )
          }
        </div>
        <div className={styles["locl-location-page-space-card__info"]}>
          <div className={styles["locl-location-page-space-card__info-main"]}>
            {
              isMediaOpportunity && (
                <div
                  className={styles["locl-location-page-space-card__media-opportunity"]}
                  data-testid="locl-space-card-media-opportunity"
                >
                  Media
                </div>
              )
            }
            <div
              className={styles["locl-location-page-space-card__name"]}
              data-testid="locl-space-card-title" // don't modify this data-testid, it require for a/b tests
            >
              { fullSpaceData.name }
            </div>
          </div>
          { renderPrice() }
        </div>
        <div className={styles["locl-location-page-space-card__footer"]}>
          <div className={styles["locl-location-page-space-card__dimensions"]}>
            { renderDimensions() }
          </div>
          <LocationSpaceCardFavourite
            campaigns={campaigns}
            popoverClassName={popoverClassName}
            isFavorite={isFavorite}
            fullSpaceData={fullSpaceData}
            onAddToCampaign={onAddToCampaign}
            handleSendAnalytics={handleSendAnalytics}
          />
        </div>
      </div>
    </Link>
  )
}

export default LocationSpaceCard;
